import { FC, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import cn from 'classnames';
import styled from 'styled-components';

import { clientConfig } from '@/config/clientConfig';
import { COLORS } from '@/shared/constants/colors';
import { useCurrencies } from '@/shared/hooks/use-currencies';
import { Input } from '@/shared/uikitV2/Input';
import { Select } from '@/shared/uikitV2/Select';
import { arrays } from '@/shared/utils/arrays';

type AssetValueInputProps = {
  label: string;
  name: string;
  required?: boolean | string;
  disabled?: boolean;
  isHighlight?: boolean;
};

export const AssetValueInput: FC<AssetValueInputProps> = ({
  label,
  name,
  required,
  disabled,
  isHighlight,
}) => {
  const { list } = useCurrencies();
  const { control } = useFormContext();

  const options = useMemo(() => {
    const sortedList = arrays.toSorted(list, (a, b) =>
      a.currencyCode.localeCompare(b.currencyCode),
    );

    return sortedList.map((o) => ({
      content: { label: o.currencyCode },
      value: o.currencyCode,
    }));
  }, [list]);

  return (
    <Container>
      <Controller
        name={`${name}.value`}
        control={control}
        defaultValue={null}
        rules={{ required }}
        render={({ field, fieldState, formState }) => (
          <Input
            {...field}
            className={cn({ highlight: isHighlight && !fieldState.isDirty })}
            type="number"
            label={label}
            required={Boolean(required)}
            status={fieldState.error ? 'error' : 'default'}
            errorText={fieldState.error?.message}
            disabled={disabled}
            onChange={(event) => {
              const raw = event.target.value;
              if (raw === '') {
                field.onChange(null);
              } else {
                const num = Number(raw);
                field.onChange(isNaN(num) ? null : num);
              }
            }}
            iconAfter={
              <Controller
                name={`${name}.from`}
                defaultValue={
                  formState.defaultValues?.[name]?.from ?? clientConfig.defaultProfileBaseCurrency
                }
                control={control}
                disabled={disabled}
                render={({ field: selectField }) => (
                  <Select withSearch className="nested-select" options={options} {...selectField} />
                )}
              />
            }
          />
        )}
      />
    </Container>
  );
};

const Container = styled.div`
  position: relative;

  .nested-select {
    .field-view {
      height: 54px;
      padding-inline: 0;
      border: none;
      background: transparent;

      .label {
        display: none;
      }

      .labels {
        width: 40px;
      }

      .selected-label {
        bottom: 15px;
      }

      .selected-label .input-transparent {
        min-width: 40px;
      }

      .icons {
        gap: 12px;
        padding-left: 0;
      }

      [role='button'] {
        display: none;
      }
    }

    [role='listbox'] {
      right: -16px;
      width: 90px;
    }

    .check {
      margin-inline: 5px 0;
    }
  }

  .highlight > .default {
    background-color: ${COLORS.systemSun100};
  }
`;
